import React, { useState } from "react";
import {
  gridRowCountSelector,
  gridVisibleColumnFieldsSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid-pro";
import { maxExportRecords } from "shared";

import { Modal } from "../../../../common/Atoms/Modal";
import { Button } from "../../../../common/Atoms/Button";
import { InputTextAddon } from "../../../../common/Atoms/InputTextAddon";
import { Paragraph } from "../../../../common/Atoms/Typography/Paragraph";
import { Notification } from "../../../../common/Atoms/Notification";
import { useUser } from "../../../hooks/useUser";

interface IExportAllModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  /** What is being exported, for the copy — e.g. `contacts` or `invoices` */
  entityLabel: string;
  /** columns is the grid's visible column fields in display order, so the CSV matches the table */
  onConfirm: (filename: string | undefined, columns: string[]) => void | Promise<void>;
}

/** Explains that the full export is emailed, with an optional custom filename */
export function ExportAllModal({ open, setOpen, entityLabel, onConfirm }: IExportAllModalProps) {
  const [filename, setFilename] = useState(``);
  const [submitting, setSubmitting] = useState(false);
  const { data: userData } = useUser();
  const apiRef = useGridApiContext();
  const rowCount = useGridSelector(apiRef, gridRowCountSelector);

  const email = userData?.email;
  const overLimit = rowCount > maxExportRecords;

  async function handleConfirm() {
    setSubmitting(true);

    try {
      await onConfirm(filename.trim() || undefined, gridVisibleColumnFieldsSelector(apiRef));
      setOpen(false);
      setFilename(``);
    }
    finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={ `Export all ${entityLabel}` }
      open={ open }
      setOpen={ setOpen }
    >
      <div className={ `space-y-4` }>
        <Paragraph variant={ `secondary` }>
          { `All pages matching your current filters will be exported as a CSV and emailed to ` }
          <span className={ `font-semibold` }>{ email || `your account email` }</span>
          { `. Larger exports can take a few minutes to arrive.` }
        </Paragraph>

        { overLimit && (
          <Notification type={ `warning` }>
            { `Your current filters match ${rowCount.toLocaleString()} rows, which is more than the export limit. Only the first ${maxExportRecords.toLocaleString()} rows will be exported.` }
          </Notification>
        ) }

        <InputTextAddon
          label={ `File name (optional)` }
          value={ filename }
          onChange={ e => setFilename(String(e.value)) }
          placeholder={ `${entityLabel}_export` }
          addOnText={ `.csv` }
          maxLength={ 80 }
        />

        <div className={ `flex justify-end gap-2` }>
          <Button
            color={ `gray` }
            onClick={ () => setOpen(false) }
          >
            { `Cancel` }
          </Button>
          <Button
            onClick={ handleConfirm }
            loading={ submitting }
          >
            { `Email export` }
          </Button>
        </div>
      </div>
    </Modal>
  );
}
